/*
 * Licensed Materials - Property of IBM
 *
 * PID 5725-H26
 *
 * Copyright IBM Corporation 2018,2020. All Rights Reserved.
 *
 * US Government Users Restricted Rights - Use, duplication or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */
import React from 'react';
import PropTypes from 'prop-types';
import { injectIntl } from 'react-intl';
import { withRouter } from 'react-router-dom';
import { FooterSection, Footer, FooterLinkList } from '@govhhs/govhhs-design-system-react';
import { RouterLink } from '@spm/core-ui';
import PATHS from '@spm/universal-access-ui/src/router/Paths';
import ApplicationFooterMessages from './ApplicationFooterMessages';
import { ID } from './constants';
import LanguageDropdown from '../LanguageDropdown';
import SDPATHS from '../../paths';
/**
 * The ApplicationFooter component handles the rendering of the Application Footer
 * feature incorporating the {@link LanguageDropdown} component to manage the local selection.
 *
 * @param intl The API to format dates, numbers and strings.
 * @param location Locations represent where the app is now, where you want it to go
 * and where it was.
 *
 * @namespace ApplicationFooter
 * @memberof features.ApplicationFooter
 */
const ApplicationFooter = ({ intl, location }) => {
  const isApplicationOrSubmssionForm =
    new RegExp(`${PATHS.APPLY}/-?\\d+`).test(location.pathname) &&
    !/confirm/.test(location.pathname);

  if ([PATHS.LOGIN, PATHS.SIGNUP].includes(location.pathname)) {
    return null;
  }
  if (
    isApplicationOrSubmssionForm ||
    [PATHS.IEGVERIFICATIONS.DETAILS].includes(location.pathname) ||
    [PATHS.IEGVERIFICATIONS.ROOT].includes(location.pathname)
  ) {
    return (
      <Footer type="small">
        <FooterSection
          ariaLabel={intl.formatMessage(ApplicationFooterMessages.footerNavigation) || 'Footer'}
          category="secondary"
          languageDropdown={<LanguageDropdown />}
          languageDropdownWidth="1/6"
        >
          <FooterLinkList listItemsWidth="1/4" listSectionWidth="5/6" />
        </FooterSection>
      </Footer>
    );
  }
  return (
    /* BEGIN Yp SD-1049 Application Footer: The footer section contains links to other screens or
     links like contact us, frequently asked questions etc. */
    <Footer type="small">
      <FooterSection
        ariaLabel={intl.formatMessage(ApplicationFooterMessages.footerNavigation) || 'Footer'}
        category="secondary"
        languageDropdown={<LanguageDropdown />}
        languageDropdownWidth="1/6"
      >
        <div className="footer-text">
          {/* SD-6471 update year to 2023 */}
          {/* SDCCA-1227 update year to 2025 */}
          <h6>© 2025 South Dakota Department of Social Services. All Rights Reserved.</h6>
        </div>
        <FooterLinkList>
          <a href="https://dss.sd.gov/" rel="noreferrer" target="_blank">
            DSS Homepage
          </a>
          <h6>|</h6>
          <a href="https://www.healthcare.gov/" rel="noreferrer" target="_blank">
            Healthcare.gov
          </a>
          <h6>|</h6>
          <RouterLink data-testid={ID.FAQ_FOOTER_ID} to={PATHS.FAQS} type="footer">
            {intl.formatMessage(ApplicationFooterMessages.footerFAQ)}
          </RouterLink>
          <h6>|</h6>
          {/** BEGIN - SD-6474 - Setting footer links to appropriate URLs and forcing open in new tab */}
          <a href="https://sd.gov/disclaim.aspx" rel="noreferrer" target="_blank">
            Disclaimer
          </a>
          <h6>|</h6>
          <a
            href="https://dss.sd.gov/keyresources/discrimination.aspx"
            rel="noreferrer"
            target="_blank"
          >
            Nondiscrimination
          </a>
          <h6>|</h6>
          <a href="https://sd.gov/accpolicy.aspx" rel="noreferrer" target="_blank">
            Accessibility
          </a>
          <h6>|</h6>
          <a href="https://sd.gov/privacy.aspx" rel="noreferrer" target="_blank">
            Privacy
          </a>
          {/** END - SD-6474 - Setting footer links to appropriate URLs and forcing open in new tab */}
          <h6>|</h6>
          <RouterLink data-testid={ID.CONTACT_US_FOOTER_ID} to={PATHS.CONTACT_US} type="footer">
            {intl.formatMessage(ApplicationFooterMessages.footerContactUs)}
          </RouterLink>
        </FooterLinkList>
        <div className="footer-text">
          <h6>700 Governors Drive | Pierre, SD 57501</h6>
        </div>
        {/** BEGIN - SD-6473 - MS - Setting footer language links to open in new window */}
        <div className="Languages-footer-text">
          <ul className="languages-ul">
            <li className="languages-list">
              <a href="https://dss.sd.gov/languages.aspx" rel="noreferrer" target="_blank">
                Español
              </a>
            </li>
            <li className="languages-list">
              <h6> | </h6>
            </li>
            <li className="languages-list">
              <a href="https://dss.sd.gov/languages.aspx" rel="noreferrer" target="_blank">
                Deutsch
              </a>
            </li>
            <li className="languages-list">
              <h6> | </h6>
            </li>
            <li className="languages-list">
              <a href="https://dss.sd.gov/languages.aspx" rel="noreferrer" target="_blank">
                繁體中文
              </a>
            </li>
            <li className="languages-list">
              <h6> | </h6>
            </li>
            <li className="languages-list">
              <a href="https://dss.sd.gov/languages.aspx" rel="noreferrer" target="_blank">
                aren
              </a>
            </li>
            <li className="languages-list">
              <h6> | </h6>
            </li>
            <li className="languages-list">
              <a href="https://dss.sd.gov/languages.aspx" rel="noreferrer" target="_blank">
                Tiếng Việt
              </a>
            </li>
            <li className="languages-list">
              <h6> | </h6>
            </li>
            <li className="languages-list">
              <a href="https://dss.sd.gov/languages.aspx" rel="noreferrer" target="_blank">
                नेपाली
              </a>
            </li>
            <li className="languages-list">
              <h6> | </h6>
            </li>
            <li className="languages-list">
              <a href="https://dss.sd.gov/languages.aspx" rel="noreferrer" target="_blank">
                Srpsko-hrvatski
              </a>
            </li>
            <li className="languages-list">
              <h6> | </h6>
            </li>
            <li className="languages-list">
              <a href="https://dss.sd.gov/languages.aspx" rel="noreferrer" target="_blank">
                አማርኛ
              </a>
            </li>
            <li className="languages-list">
              <h6> | </h6>
            </li>
            <li className="languages-list">
              <a href="https://dss.sd.gov/languages.aspx" rel="noreferrer" target="_blank">
                Sudanic Adamawa
              </a>
            </li>
            <li className="languages-list">
              <h6> | </h6>
            </li>
            <li className="languages-list">
              <a href="https://dss.sd.gov/languages.aspx" rel="noreferrer" target="_blank">
                Tagalog
              </a>
            </li>
            <li className="languages-list">
              <h6> | </h6>
            </li>
            <li className="languages-list">
              <a href="https://dss.sd.gov/languages.aspx" rel="noreferrer" target="_blank">
                한국어
              </a>
            </li>
            <li className="languages-list">
              <h6> | </h6>
            </li>
            <li className="languages-list">
              <a href="https://dss.sd.gov/languages.aspx" rel="noreferrer" target="_blank">
                Русский
              </a>
            </li>
            <li className="languages-list">
              <h6> | </h6>
            </li>
            <li className="languages-list">
              <a href="https://dss.sd.gov/languages.aspx" rel="noreferrer" target="_blank">
                Cushite Oroomiffa
              </a>
            </li>
            <li className="languages-list">
              <h6> | </h6>
            </li>
            <li className="languages-list">
              <a href="https://dss.sd.gov/languages.aspx" rel="noreferrer" target="_blank">
                Український
              </a>
            </li>
            <li className="languages-list">
              <h6> | </h6>
            </li>
            <li className="languages-list">
              <a href="https://dss.sd.gov/languages.aspx" rel="noreferrer" target="_blank">
                Français
              </a>
            </li>
          </ul>
        </div>
        {/** END - SD-6473 - MS - Setting footer language links to open in new window */}
      </FooterSection>
    </Footer>
    /* END Yp SD-1049 Application Footer: The footer section contains links to other screens or
     links like contact us, frequently asked questions etc. */
  );
};

ApplicationFooter.propTypes = {
  intl: PropTypes.object.isRequired,
  location: PropTypes.object.isRequired,
};

/**
 * Controls the rendering of the Application Footer component incorporating the
 * {@link LanguageDropdown} component to manage the locale selection.
 * @namespace
 */
export default withRouter(injectIntl(ApplicationFooter));
